// ProductTabsLayout.jsx — REMPLACEMENT de src/gestion-produits/ProductTabsLayout.jsx
// Changements :
//  • Onglets en PASTILLES (au lieu des onglets cartonnés Ant Design).
//  • Plein écran sur mobile pour TOUS les sous-onglets (la nav mobile passe
//    par le menu global, comme MobileProductsPage aujourd'hui).
//  • Wrapper .epm-desktop pour le thème de tableau du module (product-module-desktop.css).
import React from "react";
import {
  AppstoreOutlined,
  CalendarOutlined,
  FolderOpenOutlined,
  ShopOutlined,
  TagOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useIsMobile from "../hooks/useIsMobile";
import "./product-module-desktop.css";

const PRODUCT_TABS = [
  { key: "marques", label: "Marques", icon: <TagOutlined />, path: "/gestion-produits/marques", permission: "Gestion Commerciale.Produits.Marques.view" },
  { key: "categories", label: "Catégories", icon: <FolderOpenOutlined />, path: "/gestion-produits/categories", permission: "Gestion Commerciale.Produits.Categories.view" },
  { key: "produits", label: "Produits", icon: <ShopOutlined />, path: "/gestion-produits/produits", permission: "Gestion Commerciale.Produits.Produits.view" },
  { key: "services", label: "Services", icon: <ToolOutlined />, path: "/gestion-produits/services", permission: "Gestion Commerciale.Services.view" },
  { key: "rendez-vous", label: "Rendez-vous", icon: <CalendarOutlined />, path: "/gestion-produits/rendez-vous", permission: "Gestion Commerciale.RendezVous.view" },
  { key: "unites", label: "Unités", icon: <AppstoreOutlined />, path: "/gestion-produits/unites", permission: "Gestion Commerciale.Produits.Unites.view" },
];

const getAccessibleProductTabs = (hasPermission) =>
  PRODUCT_TABS.filter((tab) => hasPermission(tab.permission));

const getDefaultProductTabPath = (hasPermission) =>
  getAccessibleProductTabs(hasPermission)[0]?.path || "/accueil";

export const ProductTabsRedirect = () => {
  const { hasPermission } = useAuth();
  return <Navigate to={getDefaultProductTabPath(hasPermission)} replace />;
};

const ProductTabsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const parentOutletContext = useOutletContext();
  const { hasPermission } = useAuth();
  const isMobile = useIsMobile();

  const availableTabs = React.useMemo(
    () => getAccessibleProductTabs(hasPermission),
    [hasPermission]
  );

  const activeTab = React.useMemo(
    () =>
      availableTabs.find((tab) => location.pathname.startsWith(tab.path))?.key ||
      availableTabs[0]?.key,
    [availableTabs, location.pathname]
  );

  if (!availableTabs.length || !activeTab) {
    return <Outlet context={parentOutletContext} />;
  }

  // Mobile : chaque écran a sa propre coquille plein écran (nav via menu global).
  if (isMobile) {
    return <Outlet context={parentOutletContext} />;
  }

  return (
    <div className="epm-desktop">
      <nav className="epm-tabs" role="tablist" aria-label="Sous-modules produits">
        {availableTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={tab.key === activeTab}
            className={"epm-tab" + (tab.key === activeTab ? " is-active" : "")}
            onClick={() => tab.path !== location.pathname && navigate(tab.path)}
          >
            <span className="epm-tab__ico">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
      <div className="epm-desktop__body">
        <Outlet context={parentOutletContext} />
      </div>
    </div>
  );
};

export default ProductTabsLayout;
